import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Neutral } from '@sorare/core/src/atoms/icons/Neutral';
import { Promotion } from '@sorare/core/src/atoms/icons/Promotion';
import { Relegation } from '@sorare/core/src/atoms/icons/Relegation';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { rivals } from '@sorare/core/src/lib/glossary';

import { SeasonInterval } from 'components/rivals/seasons/SeasonInterval';

import { SeasonEntry_footballRivalsSeason } from './__generated__/index.graphql';

const Root = styled.button`
  width: 100%;

  display: flex;
  align-items: center;
  gap: 0px;
  padding: var(--double-unit);

  &:not(.disabled):hover {
    background: rgba(var(--c-rgb-neutral-1000), 0.07);
  }
  &.selected {
    background: rgba(var(--c-rgb-neutral-1000), 0.2);
  }
`;
const Wrapper = styled.div`
  width: var(--triple-unit);
`;
const Badge = styled.img`
  width: var(--triple-unit);
`;
const Info = styled(Vertical).attrs({ gap: 0, center: true })`
  flex: 1;
`;

type Props = {
  season: SeasonEntry_footballRivalsSeason;
  onClick: () => void;
  i: number;
  selected?: boolean;
};
export const SeasonEntry = ({ season, onClick, i, selected }: Props) => {
  const isLive = i === 0;
  const isPast = i === 1;
  const { ranking } = season;

  return (
    <Root onClick={onClick} className={classNames({ selected })} type="button">
      <Wrapper>
        {ranking?.divisionChange === DivisionChange.PROMOTED && (
          <Promotion fill="var(--c-rivals-friendlies)" />
        )}
        {ranking?.divisionChange === DivisionChange.RELEGATED && <Relegation />}
        {ranking?.divisionChange === DivisionChange.STABLE && <Neutral />}
      </Wrapper>
      <Info>
        {isLive && (
          <LabelM>
            <FormattedMessage {...rivals.currentSeason} />
          </LabelM>
        )}
        {isPast && (
          <LabelM>
            <FormattedMessage {...rivals.pastSeason} />
          </LabelM>
        )}
        {!(isLive || isPast) ? (
          <LabelM>
            <SeasonInterval
              startDate={season.startDate}
              endDate={season.cutOffDate}
              noBackground
            />
          </LabelM>
        ) : (
          <LabelS color="var(--c-neutral-600)">
            <SeasonInterval
              startDate={season.startDate}
              endDate={season.cutOffDate}
              noBackground
            />
          </LabelS>
        )}
      </Info>
      <Wrapper>
        {ranking?.divisionConfig.badgeUrl && (
          <Badge src={ranking.divisionConfig.badgeUrl} alt="" />
        )}
      </Wrapper>
    </Root>
  );
};

SeasonEntry.fragments = {
  footballRivalsSeason: gql`
    fragment SeasonEntry_footballRivalsSeason on FootballRivalsSeason {
      id
      slug
      startDate
      cutOffDate
      leaderboard(userSlug: $userSlug) {
        id
      }
      ranking(userSlug: $userSlug) {
        id
        divisionChange
        divisionConfig {
          id
          badgeUrl
        }
      }
    }
  ` as TypedDocumentNode<SeasonEntry_footballRivalsSeason>,
};
